import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Wrench, Shield, Clock, Users } from "lucide-react";

export default function Auth() {
  const { user } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Navigate when auth state updates
  useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error, data } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      toast.error(error.message);
      return;
    }
    // Check if user is active
    if (data.user) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("is_active")
        .eq("user_id", data.user.id)
        .maybeSingle();
      if (profile && profile.is_active === false) {
        await supabase.auth.signOut();
        setLoading(false);
        toast.error("Hesabınız devre dışı bırakılmıştır. Yönetici ile iletişime geçin.");
        return;
      }
    }
    setLoading(false);
    // Navigation handled by useEffect watching user state
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: window.location.origin,
      },
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Kayıt başarılı! Lütfen e-postanızı doğrulayın.");
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Şifre sıfırlama bağlantısı gönderildi!");
      setIsForgotPassword(false);
    }
  };

  const features = [
    { icon: Shield, title: "Güvenli Erişim", desc: "Rol tabanlı yetkilendirme" },
    { icon: Clock, title: "Gerçek Zamanlı", desc: "Anlık iş emri takibi" },
    { icon: Users, title: "Ekip Yönetimi", desc: "Teknisyen koordinasyonu" },
  ];

  const formContent = isForgotPassword ? (
    <form onSubmit={handleForgotPassword} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">E-posta</Label>
        <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
      </div>
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? "Gönderiliyor..." : "Bağlantı Gönder"}
      </Button>
      <Button type="button" variant="ghost" className="w-full" onClick={() => setIsForgotPassword(false)}>
        Giriş'e Dön
      </Button>
    </form>
  ) : (
    <>
      <form onSubmit={isLogin ? handleLogin : handleSignup} className="space-y-4">
        {!isLogin && (
          <div className="space-y-2">
            <Label htmlFor="fullName">Ad Soyad</Label>
            <Input id="fullName" value={fullName} onChange={e => setFullName(e.target.value)} required />
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="email">E-posta</Label>
          <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Şifre</Label>
          <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} />
        </div>
        {isLogin && (
          <button type="button" className="text-sm hover:underline" style={{ color: 'hsl(215, 80%, 63%)' }} onClick={() => setIsForgotPassword(true)}>
            Şifremi Unuttum
          </button>
        )}
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? "Yükleniyor..." : isLogin ? "Giriş Yap" : "Kayıt Ol"}
        </Button>
      </form>
      <div className="mt-4 text-center text-sm text-muted-foreground">
        {isLogin ? "Hesabınız yok mu?" : "Zaten hesabınız var mı?"}{" "}
        <button className="hover:underline font-medium" style={{ color: 'hsl(215, 80%, 63%)' }} onClick={() => setIsLogin(!isLogin)}>
          {isLogin ? "Kayıt Ol" : "Giriş Yap"}
        </button>
      </div>
    </>
  );

  return (
    <main className="flex min-h-screen">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-[hsl(220,25%,12%)] via-[hsl(215,80%,20%)] to-[hsl(220,25%,8%)]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-primary blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-accent blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20 text-white">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary shadow-lg shadow-primary/30">
              <Wrench className="h-6 w-6 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight">ServisYönet</h1>
          </div>
          <p className="text-lg text-white/70 mb-12 max-w-md leading-relaxed">
            Saha servis operasyonlarınızı tek platformdan yönetin. İş emirleri, müşteriler ve teknisyenlerinizi kolayca koordine edin.
          </p>
          <div className="space-y-6">
            {features.map((f) => (
              <div key={f.title} className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/10 backdrop-blur">
                  <f.icon className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <h2 className="font-semibold text-white text-base">{f.title}</h2>
                  <p className="text-sm text-white/60">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex w-full lg:w-1/2 items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md border-0 shadow-none lg:shadow-none bg-transparent">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary lg:hidden">
              <Wrench className="h-6 w-6 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl">
              {isForgotPassword ? "Şifre Sıfırlama" : isLogin ? "Giriş Yap" : "Kayıt Ol"}
            </CardTitle>
            <CardDescription>
              {isForgotPassword
                ? "E-posta adresinize sıfırlama bağlantısı göndereceğiz"
                : "Saha Servis Yönetim Sistemi"}
            </CardDescription>
          </CardHeader>
          <CardContent>{formContent}</CardContent>
        </Card>
      </div>
    </main>
  );
}
